import { API_ENDPOINTS } from "@/config";
import {
    INTEGRATION_PROTOCOL_KEYS,
    INTEGRATION_PROTOCOLS,
} from "@/components/points/integrations/integrations-v2.config";
import { Icon, Link, Skeleton, Tooltip } from "@/components";
import { api } from "@/services/real/api.service";
import type { APIPointsIntegrationDataResponse, APIPointsTaskIntegration } from "@/types/api.types";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { capitalize } from "lodash-es";
import { memo, useMemo } from "react";

interface PointsIntegrationsApiResponse {
    code: string;
    data: APIPointsIntegrationDataResponse;
}

const POINTS_INTEGRATIONS_QUERY_KEY = ["POINTS_INTEGRATIONS"] as const;
const LIQUIDITY_TASK_PATTERNS = ["LPs ", "LP ", "Liquidity "] as const;
const FALLBACK_PROTOCOL_LOGO = "/asset/token/_fallback.svg";

const sortByRank = (a: APIPointsTaskIntegration, b: APIPointsTaskIntegration) =>
    (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER);

const matchesLiquidityTask = (taskTitle: string) =>
    LIQUIDITY_TASK_PATTERNS.some((pattern) => taskTitle.includes(pattern));

const getProtocolFromTask = (task: APIPointsTaskIntegration): { title: string; logo: string } | undefined => {
    const protocolKey = task.protocolSlug
        ? INTEGRATION_PROTOCOL_KEYS.find((protocol) => task.protocolSlug.toLowerCase().includes(protocol.toLowerCase()))
        : undefined;

    if (protocolKey && protocolKey in INTEGRATION_PROTOCOLS) {
        return INTEGRATION_PROTOCOLS[protocolKey as keyof typeof INTEGRATION_PROTOCOLS];
    }

    if (task.protocolSlug) {
        return {
            title: capitalize(task.protocolSlug),
            logo: FALLBACK_PROTOCOL_LOGO,
        };
    }

    return undefined;
};

const getPointsText = (task: APIPointsTaskIntegration) => {
    const rewardMultipliers = Object.values(task.rewards ?? {}).map(({ multiplier }) => `${multiplier}x`);
    if (rewardMultipliers.length > 0) {
        return rewardMultipliers.join(" / ");
    }
    return `${task.multiplier}x`;
};

const fetchPointsIntegrations = async (): Promise<APIPointsIntegrationDataResponse> => {
    const response = await api.get<PointsIntegrationsApiResponse>(API_ENDPOINTS.POINTS.INTEGRATIONS);
    if (response.status !== 200) {
        throw new Error("Failed to fetch points integrations");
    }
    return response.data.data;
};

export const ProvideLiquidity = memo(function ProvideLiquidity() {
    const { data, isLoading } = useQuery({
        queryKey: POINTS_INTEGRATIONS_QUERY_KEY,
        queryFn: fetchPointsIntegrations,
    });

    const liquidityProviders = useMemo(() => {
        const tasks = [...(data?.passiveTasks ?? []), ...(data?.activeTasks ?? [])].sort(sortByRank);

        return tasks
            .filter((task) => matchesLiquidityTask(task.taskTitle))
            .map((task) => ({
                taskId: "taskId" in task ? task.taskId : undefined,
                taskTitle: task.taskTitle,
                LPUrl: task.LPUrl,
                protocol: getProtocolFromTask(task),
                pointsText: getPointsText(task),
            }));
    }, [data]);

    return (
        <div className="mt-4 flex flex-col w-full px-[35px] py-[25px] rounded-[8px] border-primary/20 border-[1px]">
            <h2 className="text-2xl font-medium text-primary mb-2 inline-flex gap-2 items-center">
                Provide Liquidity{" "}
                <Tooltip
                    title={
                        "Earn additional rewards by providing liquidity in the supported pools. The points indicate the multiplier for the rewards you can earn on top of regular staking."
                    }
                >
                    <InformationCircleIcon className="w-[16px] h-[16px] aspect-square text-dark stroke-[2px]" />
                </Tooltip>
            </h2>

            <div className="flex flex-col">
                {isLoading ? (
                    <div className="flex flex-col gap-3 py-4">
                        {Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                height={53}
                                className="w-full rounded-md bg-body"
                            />
                        ))}
                    </div>
                ) : liquidityProviders.length === 0 ? (
                    <div className="px-0 py-4 text-[14px] text-muted">
                        No liquidity opportunities available right now.
                    </div>
                ) : (
                    liquidityProviders.map((provider, index) => (
                        <Link
                            key={provider.taskId ?? `${provider.taskTitle}-${index}`}
                            href={provider.LPUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between gap-4 py-4 last:border-none border-b border-primary/10 cursor-pointer hover:bg-primary/10 px-2"
                        >
                            <span className="flex items-center gap-3 min-w-0 max-w-auto">
                                {provider.protocol ? (
                                    <img
                                        src={provider.protocol.logo}
                                        alt={provider.protocol.title + " icon"}
                                        className="h-5 w-5 rounded-full shrink-0"
                                        loading="lazy"
                                    />
                                ) : null}
                                <span className="text-[12px] sm:text-[14px] text-dark truncate ">
                                    {provider.taskTitle}
                                </span>
                            </span>
                            <div className="flex items-center gap-2 min-w-[100px] justify-between shrink-0">
                                <span className="flex justify-end text-right text-dark whitespace-nowrap text-[12px] sm:text-[14px] ">
                                    {provider.pointsText}
                                </span>
                                <Icon
                                    name="ArrowTopRightOnSquare"
                                    className="w-[16px] h-[16px] aspect-square rounded-full text-blue-500 stroke-[2px] hidden sm:inline-flex"
                                />
                            </div>
                        </Link>
                    ))
                )}
            </div>
        </div>
    );
});
